import ArrowForward from "@mui/icons-material/ArrowForward";
import RedemptionFooter from "@/components/redemption/RedemptionFooter";
import { GfgButton } from "@/components/ui/GfgButton";
import { GET_STARTED_URL } from "@/constants/redemption";
import type { Product } from "@/interfaces/product";
import { buildImpactReveal } from "@/utils/redemption-helper";

interface RedemptionImpactProps {
  /** The redeemed gift — its impact tag / photo / story drive the reveal copy. */
  product?: Product;
  /** Recipient's first name, appended to the "You helped …" headline when present. */
  recipientFirstName?: string;
}

/**
 * Closing impact reveal — the final screen of the recipient flow (RFW-10).
 *
 * Our Tailwind twin of the prototype's `RedemptionMessage` impact stage
 * (`.iv-msg-done`): the cause photo drops in, one personal impact headline fades, a
 * single sentence of context rises, then the conversion invitation — "Want to send
 * gifts like this?" with the "I'm ready to do more good" CTA — above the shared dark
 * GfG footer (B-Corp / Women-Owned certs). Shown once the thank-you note is sent or
 * skipped (`RedemptionSuccess` flips to it); the content animates in staged (the
 * `impact-*` animation tokens in index.css).
 */
export default function RedemptionImpact({ product, recipientFirstName }: RedemptionImpactProps) {
  const { circleSrc, headline, lead } = buildImpactReveal(product, recipientFirstName);

  return (
    <div className="absolute inset-0 z-10310 flex flex-col overflow-y-auto bg-clear-dust">
      {/* Deliberately spare: photo + one personal headline + one sentence of context. */}
      <div className="mx-auto w-full max-w-140 px-8 pt-12 pb-20 text-center max-sm:px-5.5 max-sm:pt-10">
        {circleSrc && (
          <img
            className="mx-auto mt-2 mb-7.5 block h-43 w-43 rounded-full object-cover shadow-[0_0_0_8px_var(--color-light-dust),0_16px_34px_rgba(51,40,32,0.1)] max-sm:mb-6 max-sm:h-[132px] max-sm:w-[132px] motion-safe:animate-impact-circle"
            src={circleSrc}
            alt=""
          />
        )}

        <h2 className="mb-3.5 text-balance font-crimson font-normal text-[clamp(30px,4.4vw,40px)] leading-[1.12] text-rodeo-black max-sm:text-[clamp(26px,7vw,34px)] motion-safe:animate-impact-headline">
          {headline}
        </h2>

        {lead && (
          <p className="mx-auto mb-8.5 max-w-[44ch] font-lato text-[16px] leading-[1.6] text-graphite text-pretty max-sm:text-[15px] motion-safe:animate-impact-lead">
            {lead}
          </p>
        )}

        {/* The conversion moment: the recipient is the warmest possible lead. Framed as
            a genuine invitation — a hairline divider, room to breathe, one prominent CTA. */}
        <div className="mx-auto max-w-125 border-t border-smoke pt-7.5 motion-safe:animate-impact-invite">
          <h3 className="mb-2.5 text-balance font-crimson font-normal text-[clamp(24px,3.6vw,30px)] leading-[1.15] text-rodeo-black max-sm:text-[clamp(22px,6vw,28px)]">
            Want to send gifts like this?
          </h3>
          <p className="mx-auto mb-5 max-w-100 font-lato text-[15px] leading-[1.55] text-graphite">
            Create a free account to start sending gifts with a purpose — for your team, your
            clients, or someone you love.
          </p>
          {/* TODO: route to the sender "get started" / signup flow once it exists; the
              prototype linked to client-experience-prototype.html?signup=1. Points at the
              marketing site until then. */}
          <GfgButton
            variant="primary"
            className="min-h-13 px-8 py-3.5 text-[15px] shadow-[0_8px_22px_rgba(51,40,32,0.14)] transition-[box-shadow,transform] duration-200 hover:-translate-y-px hover:shadow-[0_12px_28px_rgba(51,40,32,0.2)] motion-reduce:hover:translate-y-0 max-[640px]:px-5 max-[640px]:text-[14px]"
            endIcon={<ArrowForward sx={{ fontSize: 20 }} />}
            onClick={() => {
              window.location.href = GET_STARTED_URL;
            }}
          >
            I'm ready to do more good
          </GfgButton>
        </div>
      </div>

      {/* Shared dark GfG footer — carries the B-Corp / Women-Owned certs. Full-bleed at
          the bottom edge; mt-auto pushes it down when the content is short. */}
      <RedemptionFooter className="mt-auto w-full" />
    </div>
  );
}
